import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { FileUp, Loader2, Music } from "lucide-react";

import { LANGUAGE_OPTIONS as LANG_OPTS } from "@/config/languages";
const LANGUAGE_OPTIONS = [
  { code: "auto", label: "Auto-detect" },
  ...LANG_OPTS.map(l => ({ code: l.code, label: l.name })),
];

export default function AudioFileUploader({ token, onSessionLoaded }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("Recorded Audio Meeting");
  const [language, setLanguage] = useState("auto");
  const [targetLanguage, setTargetLanguage] = useState("none");

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selected = e.target.files[0];
      setFile(selected);
      // Auto-populate title with filename if empty/default
      if (title === "Recorded Audio Meeting") {
        setTitle(selected.name.replace(/\.[^/.]+$/, ""));
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select an audio file first");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", title);
    if (language && language !== "auto") formData.append("language", language);
    if (targetLanguage && targetLanguage !== "none") formData.append("target_language", targetLanguage);

    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const h = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await fetch(`${API}/noter/sessions/upload`, {
        method: "POST",
        headers: h,
        body: formData,
        credentials: "include",
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Transcription upload failed");
      }

      const sessionData = await res.json();
      toast.success("Transcription complete!");
      
      // Load completed session in parent
      if (sessionData && sessionData.session_id) {
        onSessionLoaded?.(sessionData.session_id);
      }
      
      // Reset form
      setFile(null);
      setTitle("Recorded Audio Meeting");
    } catch (err) {
      toast.error(err.message || "Failed to transcribe audio file");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-violet-50 to-white">
        <FileUp className="w-4 h-4 text-violet-500" />
        <h3 className="text-sm font-semibold text-slate-700">Offline Transcription</h3>
      </div>
      <form onSubmit={handleUpload} className="p-4 space-y-4">
        <div>
          <Label className="text-xs font-semibold text-slate-600">Audio File</Label>
          <div className="mt-1 flex justify-center px-4 py-4 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50/50 hover:bg-slate-50 transition-colors">
            <div className="space-y-1 text-center">
              <Music className="mx-auto h-8 w-8 text-slate-400" />
              <div className="flex text-xs text-slate-600">
                <label className="relative cursor-pointer bg-white rounded-md font-medium text-violet-600 hover:text-violet-500">
                  <span>Upload a file</span>
                  <input
                    type="file"
                    className="sr-only"
                    accept="audio/*"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-[10px] text-slate-400">
                MP3, WAV, M4A, OGG up to 50MB
              </p>
            </div>
          </div>
          {file && (
            <div className="mt-2 text-xs font-medium text-slate-700 bg-violet-50 p-2 rounded flex items-center justify-between">
              <span className="truncate max-w-[200px]">{file.name}</span>
              <span className="text-[10px] text-slate-400">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
            </div>
          )}
        </div>

        <div>
          <Label className="text-xs font-semibold text-slate-600">Meeting Title</Label>
          <Input
            className="mt-1"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="E.g., Offline Interview Notes"
            disabled={uploading}
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs font-semibold text-slate-600">Audio Language</Label>
            <Select
              value={language}
              onValueChange={setLanguage}
              disabled={uploading}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Auto-detect" />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.code} value={opt.code}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600">Translate To</Label>
            <Select
              value={targetLanguage}
              onValueChange={setTargetLanguage}
              disabled={uploading}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {LANGUAGE_OPTIONS.filter(o => o.code !== "auto").map((opt) => (
                  <SelectItem key={"upload-target-" + opt.code} value={opt.code}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button
          type="submit"
          className="w-full bg-violet-600 hover:bg-violet-700 text-white"
          disabled={uploading || !file}
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing & Transcribing...
            </>
          ) : (
            <>
              <FileUp className="w-4 h-4 mr-2" />
              Upload & Transcribe File
            </>
          )}
        </Button>
      </form>
    </div>
  );
}